import { withTopbarProps } from '@jusbrasil/topbar'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import { getFirstQueryParam } from '@jusbrasil-web/shared-utils-url'
import { GetServerSideProps } from 'next'
import { useCallback, useState } from 'react'

import { externalAuthApi, internalAuthApi } from '../../../api'
import type { SignupFormData } from '../../../api/types'
import { CompleteDataForm } from '../../../components'
import { AuthLayout } from '../../../components/auth-layout'
import { DEFAULT_ERROR_MESSAGE, OCCUPATION_IDS, ROUTES } from '../../../constants'
import {
  getBasePath,
  isStudentBenefitSignup,
  saveSignupDataToCookie,
  shouldApplyCPFBasedSignup,
} from '../../../utils'
import { withCampaignFallback } from '../../../utils/campaign-attribution'
import type { AuthPageEventData } from '../../../utils/event-data'
import { getEventDataFromQuery } from '../../../utils/event-data'
import { getVerifiedSignupEmail } from '../../../utils/get-verified-signup-email'
import { redirectTo } from '../../../utils/redirect'
import { fireSignUpFormPageSeenServer } from '../../../utils/server-side-argos-events'

export interface SignupEmailPageProps {
  basePath?: string
  token: string | null
  email: string
  nextUrl?: string | null
  csrfToken?: string | null
  shouldApplyCPFBasedSignup?: boolean
  eventData?: AuthPageEventData
  lockedOccupation?: string | null
}

export function SignupEmailPage(incomingProps?: SignupEmailPageProps) {
  const {
    basePath = '',
    token,
    email,
    nextUrl,
    shouldApplyCPFBasedSignup: shouldApplyCPF = false,
    lockedOccupation,
  } = incomingProps ?? {}
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const handleSubmit = useCallback(
    async (data: SignupFormData) => {
      if (!token) {
        setError('Token não fornecido')
        return
      }

      if (shouldApplyCPF) {
        saveSignupDataToCookie({
          name: data.fullName,
          occupation: data.occupation,
          password: data.password,
        })
        const params =
          typeof window !== 'undefined' && window.location.search
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams()
        params.set('signUpByEmailToken', encodeURIComponent(token))
        if (nextUrl) params.set('next_url', nextUrl)
        redirectTo(`${ROUTES.SIGN_UP.CPF_BASED_SIGNUP}?${params.toString()}`)
        return
      }

      setIsLoading(true)
      setError(undefined)

      try {
        const { response, data: authData } = await externalAuthApi.completeSignupWithToken(
          {
            token,
            email: data.email,
            fullName: data.fullName,
            password: data.password,
            occupation: data.occupation,
          },
          { basePath }
        )

        if (!response.ok) {
          setError(authData?.message || DEFAULT_ERROR_MESSAGE)
          return
        }

        if (authData?.mustRedirectTo) {
          redirectTo(authData.mustRedirectTo)
          return
        }

        const redirectUrl = nextUrl || '/'
        redirectTo(redirectUrl)
      } catch (err) {
        setError(err instanceof Error ? err.message : DEFAULT_ERROR_MESSAGE)
      } finally {
        setIsLoading(false)
      }
    },
    [token, nextUrl, shouldApplyCPF, basePath]
  )

  if (email == null) return null

  return (
    <AuthLayout>
      <CompleteDataForm
        email={email}
        isLoading={isLoading}
        error={error}
        onSubmit={handleSubmit}
        basePath={basePath}
        lockedOccupation={lockedOccupation}
      />
    </AuthLayout>
  )
}

const getServerSideProps: GetServerSideProps = async (ctx) => {
  const token = (ctx.params?.token as string | undefined) || null
  if (!token) {
    return {
      redirect: {
        destination: ROUTES.SIGN_UP.BEGIN,
        permanent: false,
      },
    }
  }

  let email: string | null = null

  if (token) {
    const result = await getVerifiedSignupEmail(token, ctx)
    if (!result || !result.response.ok) {
      return {
        redirect: {
          destination: ROUTES.SIGN_UP.BEGIN,
          permanent: false,
        },
      }
    }
    const { email: emailFromVerification } = result
    email = email ?? emailFromVerification
  }

  if (!email) {
    ctx.res.statusCode = 410
    return { notFound: true }
  }

  const { csrfToken } = await internalAuthApi.getCsrfToken(ctx)
  const appId = getFirstQueryParam(ctx.query.app_id) || null
  const path = ctx.resolvedUrl || ctx.req.url || ''
  const shouldApplyCPF = shouldApplyCPFBasedSignup(path, appId)
  const pathname = path.split('?')[0] ?? ''
  const basePath = getBasePath(pathname)
  const eventData = withCampaignFallback(
    getEventDataFromQuery(ctx.query, { email: email ?? undefined }),
    ctx.query,
    ctx
  )
  const nextUrl = getFirstQueryParam(ctx.query.next_url) || null
  const lockedOccupation = isStudentBenefitSignup(nextUrl) ? OCCUPATION_IDS.undergraduate : null

  fireSignUpFormPageSeenServer({
    req: ctx.req,
    email,
    eventData,
    type: 'default',
  })

  return {
    props: {
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      basePath,
      token,
      email,
      nextUrl,
      csrfToken,
      shouldApplyCPFBasedSignup: shouldApplyCPF,
      eventData,
      lockedOccupation,
    },
  }
}

export const SignupEmailPageExports = {
  getServerSideProps,
}
